import { addDoc, collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "../../../firebase";
import { STUDIO_CONNECTION_HISTORY_COLLECTION } from "../constants";

function nowIso() {
  return new Date().toISOString();
}

export async function saveConnectionVerificationHistory(entry) {
  const ref = await addDoc(collection(db, STUDIO_CONNECTION_HISTORY_COLLECTION), {
    connectionId: entry.connectionId || "",
    projectId: entry.projectId || "",
    status: entry.status || "unknown",
    healthScore: entry.healthScore ?? 0,
    services: entry.services || {},
    latency: entry.latency || {},
    verifiedAt: entry.verifiedAt || nowIso(),
    verifiedBy: entry.verifiedBy || "",
    browser: entry.browser || "",
    os: entry.os || "",
    version: entry.version || "",
    report: entry.report || null,
    createdAt: nowIso(),
  });
  return ref.id;
}

/**
 * Persists a Deep Test run to the shared studio_connection_history collection.
 * Stores the Sprint 10.1 fields (scores, timings, environment) while remaining
 * compatible with the existing history listing (verifiedAt / healthScore / status).
 */
export async function saveConnectionDeepTestHistory(entry) {
  const completedAt = entry.completedAt || nowIso();
  const ref = await addDoc(collection(db, STUDIO_CONNECTION_HISTORY_COLLECTION), {
    testType: "deep",
    connectionId: entry.connectionId || "",
    projectId: entry.projectId || "",
    startedAt: entry.startedAt || completedAt,
    completedAt,
    duration: entry.duration ?? 0,
    healthScore: entry.healthScore ?? 0,
    performanceScore: entry.performanceScore ?? 0,
    permissionScore: entry.permissionScore ?? 0,
    securityScore: entry.securityScore ?? 0,
    browser: entry.browser || "",
    os: entry.os || "",
    sdkVersion: entry.sdkVersion || "",
    verifiedBy: entry.verifiedBy || "",
    status: entry.status || "unknown",
    report: entry.report || null,
    // Compatibility fields for the existing history query/UI.
    verifiedAt: completedAt,
    latency: { total: entry.duration ?? 0 },
    version: entry.sdkVersion || "",
    createdAt: nowIso(),
  });
  return ref.id;
}

export async function listConnectionVerificationHistory(connectionId, max = 50) {
  if (!connectionId) return [];
  try {
    const snap = await getDocs(
      query(
        collection(db, STUDIO_CONNECTION_HISTORY_COLLECTION),
        where("connectionId", "==", connectionId),
        orderBy("verifiedAt", "desc"),
        limit(max)
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    const snap = await getDocs(
      query(collection(db, STUDIO_CONNECTION_HISTORY_COLLECTION), limit(max))
    );
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((h) => h.connectionId === connectionId)
      .sort((a, b) => (b.verifiedAt || "").localeCompare(a.verifiedAt || ""))
      .slice(0, max);
  }
}

export async function saveConnectionDeleteHistory({
  connectionId,
  projectId,
  deletedBy,
  reason = "",
  connectionName = "",
  firebaseProjectId = "",
}) {
  const deletedAt = nowIso();
  const ref = await addDoc(collection(db, STUDIO_CONNECTION_HISTORY_COLLECTION), {
    action: "delete_connection",
    connectionId: connectionId || "",
    projectId: projectId || "",
    connectionName,
    firebaseProjectId,
    deletedBy: deletedBy || "",
    deletedAt,
    reason: reason || "",
    createdAt: deletedAt,
    verifiedAt: deletedAt,
    status: "deleted",
  });
  return ref.id;
}
